import {
  BaseAttributes,
  ECommerceAddToCart,
  ECommerceAddToWishlist,
  ECommerceProductActionAttributes,
  ECommercePurchase,
  ECommerceRemoveFromWishlist,
} from '@trendhim/data-plans/lib/trendhim_ecom'

import {
  Event,
  EventFormat,
  EventType,
} from '@Events/Base'

type Flatten<Type> = Type extends Array<infer Item> ? Item : Type

// Product must match mParticle.eCommerce.createProduct output
export interface Product {
  Attributes: Flatten<ECommerceProductActionAttributes['products']>['custom_attributes']
  Brand: string
  Category: string
  CouponCode: string
  Name: string
  Position: number
  Price: number
  Quantity: number
  Sku: string
  TotalAmount: number
  Variant: string
}

type ProductAction = ECommerceAddToCart | ECommerceAddToWishlist | ECommercePurchase | ECommerceRemoveFromWishlist

export class ECommerceProductActionEvent<T extends ProductAction> extends Event {
  customAttributes: T['custom_attributes']

  eventName: T['eventName']

  products: Product[] | undefined

  constructor({
    custom_attributes,
    eventName,
    products,
  }: Omit<T, 'product_action'> & { products?: Product[] }) {
    super()
    this.customAttributes = custom_attributes
    this.eventName = eventName
    this.products = products
  }

  getAttributes(): BaseAttributes & T['custom_attributes'] {
    return {
      ...super.getAttributes(),
      ...this.customAttributes,
    }
  }

  getAttributesSafe(): BaseAttributes & T['custom_attributes'] {
    return super.getAttributesSafe() as BaseAttributes & T['custom_attributes']
  }

  getName(): T['eventName'] {
    return this.eventName
  }

  getType(): EventType {
    return 'ECommerce'
  }

  transform(): EventFormat {
    if (!this.products || this.products.length === 0) {
      throw new Error(`missing products: '${this.getName()}'`)
    }

    switch (this.getName()) {
      case 'add_to_cart': {
        return [
          'mParticle.eCommerce.logProductAction',
          [
            1,
            this.products,
            this.getAttributesSafe(),
          ],
        ]
      }
      case 'add_to_wishlist': {
        return [
          'mParticle.eCommerce.logProductAction',
          [
            9,
            this.products,
            this.getAttributesSafe(),
          ],
        ]
      }
      case 'remove_from_wishlist': {
        return [
          'mParticle.eCommerce.logProductAction',
          [
            10,
            this.products,
            this.getAttributesSafe(),
          ],
        ]
      }
      default: throw new Error(`unsupported name: '${this.getName()}'`)
    }
  }
}
